import React, { useState, useEffect, useRef } from 'react'
import styled, { keyframes } from 'styled-components'
import MemberSummary from './member-summary'
import FeaturedMember from './featured-member'
import Spinner from '../spinner'
import { useScrollReset } from '../scroll'
import { sort } from 'ramda'
import { inheritPrimaryColor } from '../colors'

const defaultPic = `/public/defaultuser.png`

const preloadedMembers = typeof window !== 'undefined' && window.__data__ && window.__data__.members
const preloadedTeamPics = typeof window !== 'undefined' && window.__data__ && window.__data__.team_pics

const TeamCarousel = styled(({ imageUrls, imageCaptions, ...props }) => {
  const [id, setId] = useState(0)
  const intervalId = useRef()
  const previous = () => {
    setId(id => id === 0 ? imageUrls.length - 1 : id - 1)
  }
  const next = () => {
    setId(id => id === imageUrls.length - 1 ? 0 : id + 1)
  }
  const clear = () => {
    if (intervalId.current) clearInterval(intervalId.current)
  }
  useEffect(() => {
    intervalId.current = setInterval(next, 8000)
    return clear
  }, [])

  return (
    <div {...props}>
      <ImageContainer key={imageUrls[id]} url={imageUrls[id]} />
      {imageCaptions[id] && <Caption>
        <div css='color: white; max-width: 1140px; align-self: center;'>{imageCaptions[id]}</div>
      </Caption>}
      <ChangeId onClick={() => (previous(), clear())} direction='back' />
      <ChangeId onClick={() => (next(), clear())} direction='forward'/>
    </div>
  )
})`
  align-self: stretch;
  margin-left: -20px;
  margin-right: -20px;
  height: 450px;
  position: relative;
  margin-bottom: 40px;
  overflow: hidden;
  display: flex;
  background-color: black;
`
const Caption = styled.div`
  background-color: rgba(30, 30, 30, 0.5);
  padding: 20px;
  display: flex;
  flex-direction: column;
  position: absolute;
  bottom: 0px;
  right: 0px;
  left: 0px;
`
const Arrow = styled.div`
  border-radius: 50%;
  background-color: ${inheritPrimaryColor};
  color: white;
  width: 32px;
  height: 32px;
  transition: transform 0.2s;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid white;
`
const ChangeId = styled.div.attrs(p => ({ children: <Arrow className='material-icons'>{p.direction === 'back' ? 'keyboard_arrow_left' : 'keyboard_arrow_right'}</Arrow> }))`
  padding: 20px;
  position: absolute;
  top: 0px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 2.2em;
  bottom: 0px;
  ${p => p.direction === 'back' ? 'left: 0px;' : 'right: 0px;'}
  font-weight: 500;
  cursor: pointer;
  &:hover > div {
    transform: scale(1.2);
  }
`
const ImagesContainer = styled.div`

`
const animation = keyframes`
  0% { opacity: 0; transform: translate3d(1000px, 0px, 0px); }
  100% { opacity: 1 }
`
const ImageContainer = styled.div`
  flex-grow: 1;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  background-image: url("${p => p.url}");
  animation-name: ${animation};
  animation-duration: 0.8s;
`

const AllMembers = styled(({ data, ...props }) => {
  useScrollReset()
  const members = data ? data.members : preloadedMembers
  const teamPics = data ? data.team_pics : preloadedTeamPics

  const imageUrlCache = useRef()
  useEffect(() => {
    if (teamPics && !imageUrlCache.current) {
      imageUrlCache.current = {}
      teamPics.forEach(({ guid }) => {
        const img = new Image()
        img.src = guid
        imageUrlCache.current[guid] = img
      })
    }
    return () => imageUrlCache.current = undefined
  }, [teamPics])

  if (!members || !teamPics) return <div css='margin-top: 30px' {...props}><Spinner /></div>

  const currentMembers = members.filter(({ acf: { alumni } }) => !alumni)
  const alumniMembers = members.filter(({ acf: { alumni } }) => alumni)

  const currentMembersSorted = sort((a, b) => a.slug.localeCompare(b.slug), currentMembers).filter(({ slug }) => slug !== 'serena-nik-zainal')
  const alumniMembersSorted = sort((a, b) => a.slug.localeCompare(b.slug), alumniMembers)

  const featuredMember = currentMembers.find(({ slug }) => slug === 'serena-nik-zainal')

  return (
    <div {...props}>
      <FeaturedMember member={featuredMember} />
      <TeamCarousel imageUrls={teamPics.map(({ guid }) => guid)} imageCaptions={teamPics.map(({ post_excerpt }) => post_excerpt)} />
      <div css='max-width: 1140px; display: flex; flex-wrap: wrap; justify-content: center;'>
        {currentMembersSorted.map(
          ({
            slug,
            title: { rendered: name },
            acf: { job_title: jobTitle, picture }
          }) => <MemberSummary slug={slug} key={slug} name={name} jobTitle={jobTitle} picture={picture || defaultPic} />
        )}
      </div>
      <div css='max-width: 1140px; margin-top: 20px; margin-bottom: 50px;'>
        <AlumniHeader/>
        <div css='display: flex; flex-wrap: wrap;'>
          {alumniMembersSorted.map(
            ({
              slug,
              title: { rendered: name },
              acf: { job_title: jobTitle }
            }) => <AlumniMemberSummary slug={slug} key={slug} name={name} jobTitle={jobTitle} />
          )}
        </div>
      </div>
    </div>
  )
})`
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 20px;
`

const AlumniMemberSummary = styled(({ name, jobTitle, slug, ...props }) => {
  return <div {...props}>
    <AlumniName>{name}</AlumniName>
    <div css='color: rgb(150, 150, 150);'>{jobTitle}</div>
  </div>
})`
  margin: 20px;
  padding-left: 10px;
  border-left: 1px solid ${inheritPrimaryColor};
`

const AlumniName = styled.div`color: ${inheritPrimaryColor}; font-weight: bold;`

const AlumniHeader = styled.h1.attrs({ children: 'Alumni' })`color: ${inheritPrimaryColor}`

export default AllMembers